import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Trash2, ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export const PrivacySection = ({ onAccountDeleted }) => {
  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/user/export-my-data`, { timeout: 60000 });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PROXIMA_mis_datos_${new Date().toISOString().split("T")[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Tus datos se descargaron correctamente.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al exportar tus datos.");
    }
    setExporting(false);
  };

  const handleDelete = async () => {
    if (confirmText !== "ELIMINAR MI CUENTA") {
      toast.error('Escribe exactamente "ELIMINAR MI CUENTA" para confirmar.');
      return;
    }
    setDeleting(true);
    try {
      await axios.post(`${API}/user/delete-account`, { confirmation: confirmText }, { timeout: 60000 });
      toast.success("Tu cuenta fue eliminada. Adios.");
      setTimeout(() => {
        try { localStorage.clear(); } catch (e) { /* ignore */ }
        if (onAccountDeleted) onAccountDeleted();
        window.location.href = "/";
      }, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar cuenta.");
    }
    setDeleting(false);
  };

  return (
    <div className="rounded-2xl bg-[#0A0A0F] border border-white/[0.08] p-6 mb-6" data-testid="privacy-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-xl bg-cyan-500/10 flex items-center justify-center">
          <ShieldCheck className="w-4 h-4 text-cyan-400" />
        </div>
        <div>
          <h2 className="text-sm font-bold text-white">Privacidad y Datos</h2>
          <p className="text-xs text-gray-500">Tus derechos sobre los datos que PROXIMA almacena de ti</p>
        </div>
      </div>

      <div className="space-y-2">
        <button
          onClick={handleExport}
          disabled={exporting}
          className="w-full flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04] hover:border-cyan-500/20 transition-all text-left disabled:opacity-60"
          data-testid="export-my-data-btn"
        >
          {exporting ? <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" /> : <Download className="w-5 h-5 text-cyan-400" />}
          <div className="flex-1">
            <p className="text-sm text-white font-medium">Exportar mis datos</p>
            <p className="text-xs text-gray-500">Descarga un JSON con todo lo que PROXIMA tiene de ti (portabilidad)</p>
          </div>
        </button>

        <button
          onClick={() => setDeleteOpen(true)}
          className="w-full flex items-center gap-4 p-4 rounded-xl bg-red-500/5 border border-red-500/10 hover:bg-red-500/10 transition-all text-left"
          data-testid="delete-account-btn"
        >
          <Trash2 className="w-5 h-5 text-red-400" />
          <div className="flex-1">
            <p className="text-sm text-white font-medium">Eliminar mi cuenta</p>
            <p className="text-xs text-gray-500">Borra permanentemente todos tus datos (derecho al olvido)</p>
          </div>
        </button>
      </div>

      {deleteOpen && (
        <div className="mt-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30" data-testid="delete-confirmation">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-100">
              <p className="font-semibold mb-1">Esta accion es irreversible.</p>
              <p className="text-xs text-red-200/80">Se eliminaran tu cuenta, todos tus documentos, briefs, analisis IA, configuraciones de marca, sesiones, suscripciones y datos asociados. Los backups del sistema pueden retener una copia por hasta 30 dias adicionales.</p>
            </div>
          </div>
          <label className="block text-xs text-gray-400 mb-1">Escribe <span className="font-mono text-red-300">ELIMINAR MI CUENTA</span> para confirmar:</label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-black/40 border border-red-500/30 text-white text-sm font-mono focus:outline-none focus:border-red-500"
            placeholder="ELIMINAR MI CUENTA"
            data-testid="delete-confirm-input"
          />
          <div className="flex gap-2 mt-3">
            <button onClick={() => { setDeleteOpen(false); setConfirmText(""); }} className="px-4 py-2 rounded-lg bg-white/5 text-gray-300 text-xs hover:bg-white/10 transition-colors" data-testid="delete-cancel">Cancelar</button>
            <button onClick={handleDelete} disabled={deleting || confirmText !== "ELIMINAR MI CUENTA"} className="px-4 py-2 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-testid="delete-confirm-btn">
              {deleting ? "Eliminando..." : "Eliminar permanentemente"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacySection;
